import { z } from 'zod';
import { FormContentType } from '../../constants';
import { contentCriterionSchema, type ContentCriterionInput } from './criterion';
import { questionRefSchema } from './question-data';
import { cardDataSchema, type CardDataInput } from './card-data';
import { contactDataSchema, type ContactDataInput } from './contact-data';

export const formStepContentDataSchema = z.union([
  z.array(questionRefSchema),
  cardDataSchema,
  contactDataSchema,
  z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))).length(0),
]);
export type FormStepContentDataInput = z.infer<typeof formStepContentDataSchema>;

export type FormStepContentInput = {
  id?: number;
  order: number;
  type: string;
  data?: FormStepContentDataInput;
  nested_questions?: FormStepContentInput[];
  criteria?: ContentCriterionInput[];
  form_step_id?: number;
};

export const formStepContentSchema: z.ZodType<FormStepContentInput> = z.lazy(() =>
  z
    .object({
      id: z.number().int().positive().optional(),
      order: z.number().int(),
      type: z.string().max(32),
      data: formStepContentDataSchema.default([]),
      nested_questions: z.array(formStepContentSchema).default([]),
      criteria: z.array(contentCriterionSchema).default([]),
      form_step_id: z.number().int().positive().optional(),
    })
    .superRefine((content, ctx) => {
      if (content.type === FormContentType.QUESTION) {
        if (!Array.isArray(content.data) || content.data.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['data'],
            message: 'Select at least one question in this block',
          });
        }
        return;
      }

      if (FormContentType.CONTACT === content.type) {
        const data = content.data as ContactDataInput | undefined;
        const enTitle = data && !Array.isArray(data) ? data.i18n?.en?.title : undefined;
        if (!enTitle?.trim()) {
          ctx.addIssue({
            code: 'custom',
            path: ['data', 'i18n', 'en', 'title'],
            message: 'Header title (EN) is required',
          });
        }
        return;
      }

      if (content.type === FormContentType.CARD) {
        const data = content.data as CardDataInput | undefined;
        const hasContent = Boolean(
          data &&
          !Array.isArray(data) &&
          (data.i18n?.en?.title?.trim() || data.i18n?.en?.description?.trim() || data.image?.trim())
        );
        if (!hasContent) {
          ctx.addIssue({
            code: 'custom',
            path: ['data'],
            message: 'Card must have at least a title (EN), a description (EN) or an image',
          });
        }
      }
    })
);
